import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useHostnameTenant } from '../context/HostnameTenantContext';

/**
 * CleanUrlRedirector — used inside the slug-prefixed Route tree.
 *
 * When the hostname has been matched to a tenant (status === 'matched'), any
 * URL that still contains the EXACT slug prefix (e.g. /mvg/dashboard) is
 * redirected to the clean URL (/dashboard) so that old bookmarks work
 * transparently.
 *
 * Only redirects when the first path segment matches the known tenant slug
 * exactly, preventing arbitrary first-segment stripping.
 *
 * The redirect uses `replace` so the slug-prefixed URL is not pushed to the
 * browser history stack.
 *
 * This component renders nothing; it only has side effects.
 */
const CleanUrlRedirector = () => {
  const { status, tenant } = useHostnameTenant();
  const { tenantSlug } = useParams();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Only act in matched mode with a known slug, and only when the URL's
    // first segment exactly matches that slug (never strip an arbitrary segment).
    if (status !== 'matched' || !tenant?.slug || tenantSlug !== tenant.slug) return;

    const slugPrefix = `/${tenant.slug}`;

    if (pathname.startsWith(slugPrefix)) {
      const cleanPath = pathname.slice(slugPrefix.length) || '/';
      navigate(
        { pathname: cleanPath, search, hash },
        { replace: true }
      );
    }
  }, [status, tenant, tenantSlug, pathname, search, hash, navigate]);

  return null;
};

export default CleanUrlRedirector;
